import React, { useCallback, useState } from 'react';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';

import FlexipassLogo from '../../../public/images/flexipass-salto.svg';

import { BottomSheet } from '../../theme';
import { DoorList } from './DoorList';
import { DoorState } from './DoorState';
import { FlexipassDoor, RemotelockPin, Settings } from '../../../models';

import styles from './DigitalKeysBottomSheet.module.scss';
import { PinCodesList } from './PinCodesList';

interface DigitalKeysBottomSheetProps {
  open: boolean;
  settings?: Settings;
  onDoorOpen?: (doorId: number) => Promise<any>;
  onClose: () => void;
  type: 'flexipass' | 'remotelock';
  items?: FlexipassDoor[] | RemotelockPin[];
}

export const DigitalKeysBottomSheet = ({
  type,
  settings,
  open,
  items,
  onClose,
  onDoorOpen,
}: DigitalKeysBottomSheetProps) => {
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState(false);
  const [currentDoor, setCurrentDoor] = useState<FlexipassDoor | null>(null);

  const handleDoorOpen = useCallback(
    async (door: FlexipassDoor) => {
      setOpening(true);
      setError(false);
      setCurrentDoor(door);

      try {
        const result = await onDoorOpen?.(door.id);
        setError(!result?.openingSuccess);
      } catch {
        setError(true);
      } finally {
        setOpening(false);
      }
    },
    [onDoorOpen],
  );

  const handleClose = () => {
    setCurrentDoor(null);
    setOpening(false);
    onClose();
  };

  const renderContent = useCallback(() => {
    // Render flexipass doors
    if (type === 'flexipass') {
      return (
        <>
          {currentDoor ? (
            <DoorState opening={opening} error={error} door={currentDoor} />
          ) : (
            <DoorList
              doors={items as FlexipassDoor[]}
              onClick={handleDoorOpen}
            />
          )}
          <footer className={styles.footer}>
            <FlexipassLogo height={24} width={150} alt="FLEXIPASS + Salto" />
          </footer>
        </>
      );
    }
    // Else render remotelock Pins
    return (
      <PinCodesList
        codes={items as RemotelockPin[]}
        helpText={settings?.remotelock?.help_text}
      />
    );
  }, [type, items, currentDoor, opening, error, handleDoorOpen]);

  return (
    <BottomSheet
      open={open}
      onClose={handleClose}
      paperStyle={{
        borderTopLeftRadius: 40,
        borderTopRightRadius: 40,
        maxHeight: '70%',
        maxWidth: 600,
        margin: 'auto',
      }}
    >
      {!opening && (
        <IconButton
          aria-label="close"
          style={{ position: 'absolute', right: 0, top: 0, margin: 12 }}
          onClick={handleClose}
          size="large"
        >
          <CloseIcon style={{ fill: 'black' }} />
        </IconButton>
      )}

      {renderContent()}
    </BottomSheet>
  );
};
